import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import { SelectInput } from "~/components/SelectInput";
import { CitationSort, CitationTypeFilter } from "./types";

// // // //

interface Props {
    hasActiveFilters: boolean;
    search: string;
    typeFilter: CitationTypeFilter;
    sort: CitationSort;
    onClearFilters: () => void;
    onSearchChange: (value: string) => void;
    onTypeFilterChange: (value: CitationTypeFilter) => void;
    onSortChange: (value: CitationSort) => void;
}

/**
 * Header for the all-citations section with search, type, and sort controls.
 */
export function CitationsSectionHeader(props: Props) {
    const {
        hasActiveFilters,
        search,
        typeFilter,
        sort,
        onClearFilters,
        onSearchChange,
        onTypeFilterChange,
        onSortChange,
    } = props;

    return (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
                <h2 className="flex items-center gap-2 text-2xl font-light text-foreground">
                    All Publications
                </h2>
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={onClearFilters}
                        className="px-2 text-xs font-normal text-primary mt-0.5"
                    >
                        Clear Search
                    </button>
                )}
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 lg:flex-nowrap xl:w-2/3">
                <div className="relative flex w-full items-center lg:w-auto lg:grow">
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="pointer-events-none absolute left-3 h-3.5 w-3.5 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search titles and publishers"
                        className="h-10 w-full rounded-lg border-border bg-card pl-8.5 pr-3 text-[13px] font-light text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary"
                    />
                </div>

                <SelectInput
                    value={typeFilter}
                    size="md"
                    className="w-full md:flex-1 lg:w-[170px] lg:flex-none"
                    placeholderPosition="hidden"
                    optionsContainerAlign="end"
                    options={[
                        { value: "all", content: { label: "All types" } },
                        {
                            value: "institutional",
                            content: { label: "Institutional Report" },
                        },
                        {
                            value: "media",
                            content: { label: "Media" },
                        },
                    ]}
                    onChange={(value) => {
                        onTypeFilterChange(value as CitationTypeFilter);
                    }}
                />

                <SelectInput
                    value={sort}
                    size="md"
                    placeholderPosition="hidden"
                    className="w-full md:flex-1 lg:w-[150px] lg:flex-none"
                    options={[
                        {
                            value: "newest",
                            content: { label: "Newest first" },
                        },
                        {
                            value: "oldest",
                            content: { label: "Oldest first" },
                        },
                        {
                            value: "publisher",
                            content: { label: "Publisher A–Z" },
                        },
                    ]}
                    onChange={(value) => {
                        onSortChange(value as CitationSort);
                    }}
                />
            </div>
        </div>
    );
}
